/* eslint-disable array-callback-return */
/* eslint-disable no-param-reassign */
import { useTheme } from '@emotion/react';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import InactivityTracker from 'components/InactivityTracker';
import Sidebar from 'components/Sidebar/Sidebar';
import { MyContext } from 'Context';
import { useSidebar } from 'Context/SidebarProvider';
import { getLocalStorageItem, setLocalStorageItem } from 'hooks';
import { useAuthMe } from 'queries/AuthQueries';
import { SaveFCMTokenMutation } from 'queries/Users';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DependentMenu,
  PatientMenu,
  PracticeAdmin,
  routes,
  SuperAdminMenu,
  UserMenu,
} from 'routes/RouteConstants';

import { getToken } from '../../firebaseInit';

// environment variables with fallbacks
const dashboardIconPath = process.env.REACT_APP_DASHBOARD_ICON_PATH || '';
const mobileIconPath = process.env.REACT_APP_MOBILE_ICON_PATH || '';

const Layout = ({ children }: any) => {
  const theme: any = useTheme();
  const { i18n } = useTranslation();
  const { data: profile } = useAuthMe();
  const underSm = useMediaQuery(theme.breakpoints.down('md'));
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const [strokeColor, setStrokeColor] = useState<any>(
    underSm ? UserMenu : PracticeAdmin
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const userRoleGuard = getLocalStorageItem('adminRoles');
  const { isDependent, shouldSideBarOpenable } = useContext(MyContext);

  const handleSidebarColor = (val: any) => {
    const updatedStrokeColor = strokeColor.map((item: any) => ({
      ...item,
      isSelected: item.name === val,
    }));
    setStrokeColor(updatedStrokeColor);
  };
  const rolesdata2 = getLocalStorageItem('adminRoles');

  useEffect(() => {
    const rolesLayout = getLocalStorageItem('roles');
    const rolesdata = getLocalStorageItem('adminRoles');
    const roleAccess = profile?.data?.account?.patientsAccessKey || null;

    const filteredMenu =
      roleAccess === null
        ? PracticeAdmin.filter(
            (tab) => tab.name !== 'Patients' && tab.name !== 'Inbox'
          )
        : PracticeAdmin;

    const filteredUserMenu =
      roleAccess === null
        ? UserMenu.filter(
            (tab) => tab.name !== 'Patients' && tab.name !== 'Inbox'
          )
        : UserMenu;

    if (isDependent) {
      setStrokeColor(DependentMenu);
    } else if (
      rolesLayout?.roles?.includes('practice_admin') &&
      !rolesLayout?.roles?.includes('super_admin')
    ) {
      setStrokeColor(() => (underSm ? filteredUserMenu : filteredMenu));
    } else if (rolesLayout?.roles?.includes('patient')) {
      setStrokeColor(PatientMenu);
    } else if (
      rolesLayout?.roles?.includes('practice_admin') &&
      rolesLayout?.roles?.includes('super_admin')
    ) {
      setStrokeColor(() =>
        rolesdata?.isSuperAdmin ? SuperAdminMenu : filteredMenu
      );
    } else if (
      !rolesLayout?.roles?.includes('practice_admin') &&
      rolesLayout?.roles?.includes('super_admin')
    ) {
      setStrokeColor(SuperAdminMenu);
    } else if (rolesLayout?.roles?.includes('general_user')) {
      setStrokeColor(filteredUserMenu);
    }
  }, [underSm, profile?.data?.account?.patientsAccessKey, isDependent]);

  const { mutate: saveFCMTokenMutation } = SaveFCMTokenMutation();

  useEffect(() => {
    let data: string;

    async function FCMTOKEN() {
      // @ts-ignore
      data = await getToken();
      if (data) {
        saveFCMTokenMutation({ fcmToken: data });
      }
      return data;
    }

    FCMTOKEN();
  }, []);

  const userData = getLocalStorageItem('roles');

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const switchLanguage = (language: string) => {
    i18n.changeLanguage(language);
    setLocalStorageItem('lang', language);
    setAnchorEl(null);
  };

  const { isSidebarMinimized, toggleSidebar, setIsSidebarMinimized } =
    useSidebar();

  let sidebarWidth;
  let contentWidth;

  if (underSm) {
    sidebarWidth = '0';
    contentWidth = '100%';
  } else {
    sidebarWidth = isSidebarMinimized ? '100px' : '20%';
    contentWidth = isSidebarMinimized ? 'calc(100% - 100px)' : '80%';
  }

  const navigateUserHome = () => {
    if (userData?.roles?.includes('patient')) {
      navigate(routes.REQUESTS.pathname);
    } else if (
      userData?.roles.includes('practice_admin') &&
      !userData?.roles.includes('super_admin')
    ) {
      navigate(routes.MESSAGING.pathname);
    } else if (
      !userData?.roles.includes('practice_admin') &&
      userData?.roles.includes('super_admin')
    ) {
      navigate(routes.MANAGE_CLINICS.pathname);
    } else if (
      userData?.roles.includes('practice_admin') &&
      userData?.roles.includes('super_admin')
    ) {
      if (userRoleGuard?.isSuperAdmin) {
        navigate(routes.MANAGE_CLINICS.pathname);
      } else if (userRoleGuard?.isAdmin) {
        navigate(routes.DASHBOARD.pathname);
      } else if (!userRoleGuard?.isSuperAdmin && !userRoleGuard?.isAdmin) {
        navigate(routes.ROLE_LOGIN.pathname);
      }
    } else {
      navigate(routes.MESSAGING.pathname);
    }
  };

  useEffect(() => {
    setIsSidebarMinimized(!shouldSideBarOpenable);
  }, [shouldSideBarOpenable]);

  return (
    <>
      {(userData?.roles?.includes('general_user') ||
        userData?.roles?.includes('practice_admin')) && (
        <InactivityTracker inactivityTime={10000} />
      )}
      <div
        className={`relative flex w-full bg-[#11111F] ${
          location?.pathname === '/messaging' && underSm ? 'fixed' : ''
        }`}
      >
        <div className="absolute z-[99] hidden h-[100%] w-[100%] bg-[#00000073]"></div>
        <div
          style={{ width: sidebarWidth }}
          className={`transition-width fixed z-50 h-screen bg-[#11111F] duration-300 `}
        >
          {!underSm && (
            <>
              <div
                className={`flex cursor-pointer px-[16px] ${
                  isSidebarMinimized ? 'flex-col ' : 'flex-row'
                }
      mt-2 items-center justify-between`}
              >
                <div
                  className={`${
                    underSm || isSidebarMinimized ? 'pt-1' : ''
                  } px-[16px] py-[8px]`}
                >
                  <img
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateUserHome();
                    }}
                    src={
                      isSidebarMinimized ? mobileIconPath : dashboardIconPath
                    }
                    alt="Logo"
                    className={
                      isSidebarMinimized ? 'size-[60px]' : 'h-[26px] w-[160px]'
                    }
                  />
                </div>
                <div
                  className={`flex ${
                    isSidebarMinimized ? 'flex-col' : 'flex-row'
                  }
      items-center justify-between`}
                >
                  <div
                    className={`
  ${
    isSidebarMinimized
      ? 'mt-2 flex h-10 w-12 items-center justify-center rounded-md bg-secondary-100 px-3 py-0'
      : ''
  }
`}
                  >
                    <IconButton
                      aria-controls="language-menu"
                      aria-haspopup="true"
                      onClick={handleOpenMenu}
                      color="inherit"
                    >
                      <LanguageIcon
                        className="mb-1 text-white"
                        fontSize="medium"
                      />
                    </IconButton>
                    <Menu
                      id="language-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                      sx={{
                        '& .MuiMenu-list': {
                          paddingTop: 0,
                          paddingBottom: 0,
                        },
                      }}
                    >
                      <MenuItem
                        className="text-xs"
                        style={{ padding: '6px 12px' }}
                        onClick={() => switchLanguage('de')}
                      >
                        Deutsch
                      </MenuItem>
                      <Divider style={{ margin: 0 }} />
                      <MenuItem
                        className="text-xs"
                        style={{ padding: '6px 12px' }}
                        onClick={() => switchLanguage('en')}
                      >
                        English
                      </MenuItem>
                    </Menu>
                  </div>

                  {/* {!rolesdata2?.isSuperAdmin && (
                    <div
                      className={`
  ${
    isSidebarMinimized
      ? 'mt-2 flex h-10 w-12 items-center justify-center rounded-md bg-secondary-100 px-3 py-0 pt-1'
      : ''
  }
`}
                    >
                      <PopHover />
                    </div>
                  )} */}
                </div>
              </div>
            </>
          )}

          <Sidebar
            setToggel={setShowSidebar}
            toggel={showSidebar}
            options={strokeColor || []}
            handleStroke={(ele: any) => {
              handleSidebarColor(ele);
            }}
            isSidebarMinimized={isSidebarMinimized}
            toggleSidebar={toggleSidebar}
          />
        </div>
        <div
          style={{ marginLeft: sidebarWidth, width: contentWidth }}
          className={`content transition-margin ${
            !underSm ? 'mt-4' : ''
          } duration-300`}
        >
          <div
            style={{ height: 'calc(100vh - 16px' }}
            className={`overflow-hidden ${
              underSm ? 'bg-white' : 'rounded-tl-2xl bg-white'
            }`}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default Layout;
